import React, { useState, useEffect } from 'react';

import { Popover } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterItem,
  CharacterBasicItem,
  Money,
  CharacterAdvantage,
  CharacterClass,
  Spell,
  CharacterAction,
  RolledValue,
  SKILLS,
} from "@dndModels";
import { 
  COST_UNIT_MAP, 
  TIME_UNIT_MAP, 
  TIME_UNIT_MAP_SINGULAR 
} from "@dndModels/Constants";

import API from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import CommonUtilities from "@utilities/common_utilities";

import ButtonBox from "@components/input/buttons/ButtonBox";

import DandDButtonBox from "@dndComponents/DandDButtonBox";
import Roller from "@dndComponents/other/Roller";
import CharConnectUtilities from '@dndUtilities/char_connect_utilities';

interface Props {
  character: Character;
  char_item: CharacterItem;
  action?: CharacterAction;
  shared: boolean;
  // onChange: (repopulate: boolean, close: boolean) => void;
  // onTransform: () => void;
  onRemove: () => void;
}

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();
const connect_util = CharConnectUtilities.getInstance();

export default function CharacterItemScrollControls({
  character,
  char_item,
  // onChange,
  onRemove
}: Props) {
  const [reloading, setReloading] = useState(false);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLDivElement | null>(null);
  const [roll, setRoll] = useState<RolledValue[]>([]);
  const [ping, setPing] = useState(false);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const enabled = api.user && (character.owner_id === api.user._id || (character.campaign && character.campaign.owner_id === api.user._id));

  const addSpell = (char_class: CharacterClass, spell: Spell) => {
    connect_util.addSpellToBook(character, spell, char_class, true);
    // onChange(false, false);
    character.changed();
    character.subscription_holder.itemsChanged();
    setPing(!ping);
  }
  
  const renderExtras = () => {
    return (
      <>
        <Popover
          open={ popoverAnchorEl !== null }
          anchorEl={popoverAnchorEl}
          onClose={() => {
            setPopoverAnchorEl(null);
          }}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          { popoverAnchorEl !== null && renderRoll() }
        </Popover>
      </>
    );
  }
  
  const renderRoll = () => {
    if (char_item.item_type === "Scroll" && char_item.extra_spell) {
      const spell = char_item.extra_spell;
      // Rolls an Arcana check, and tells you if you succeeded
      const skill = SKILLS.find(o => o.name === "Arcana");
      const dc = 10 + spell.level;

      if (skill) {
        return (
          <Roller 
            name={skill.name}
            rolled_values={roll} 
            type="Skill Check" 
            dc={dc}
          />
        );
      }
    }
  }

  if (char_item.item_type === "Scroll" && char_item.extra_spell && char_item.extra_spell.level > 0) {
    // debugger;
    const spell = char_item.extra_spell;
    const spellbook_classes = character.spirit.classes.filter(o => o.spellbook && 
      o.spell_level_max >= spell.level &&
      o.spellbook.spellbook && common_util.anyOverlaps(o.spell_list_ids, spell.spell_list_ids));
    if (spellbook_classes.length > 0) {
      return (
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 12
          }}>
          { spellbook_classes.map((char_class, key) => {
            const char_spellbook = char_class.spellbook;
            if (char_spellbook && char_spellbook.spells.filter(o => o.spell_id === spell._id).length === 0) {
              const mods = char_spellbook.modifiers.filter(o => o.schools.length === 0 || o.schools.includes(spell.school));
              let time = char_spellbook.time_num_per_spell_level;
              let time_unit = char_spellbook.time_unit;
              let time_unit_str = "";
              let cost = char_spellbook.cost_per_spell_level;
              let cost_unit = char_spellbook.cost_unit;
              for (const mod of mods) {
                if (mod.time_unit < time_unit) {
                  time = mod.time_num_per_spell_level;
                  time_unit = mod.time_unit;
                } else if (mod.time_unit === time_unit && mod.time_num_per_spell_level < time) {
                  time = mod.time_num_per_spell_level;
                }
                if (mod.cost_unit < cost_unit) {
                  cost = mod.cost_per_spell_level;
                  cost_unit = mod.cost_unit;
                } else if (mod.cost_unit === cost_unit && mod.cost_per_spell_level < cost) {
                  cost = mod.cost_per_spell_level;
                }
              }
        
              cost *= spell.level;
              time *= spell.level;
              if (time === 1) {
                time_unit_str = TIME_UNIT_MAP_SINGULAR[time_unit];
              } else {
                time_unit_str = TIME_UNIT_MAP[time_unit];
              }
        
              const skill = SKILLS.find(o => o.name === "Arcana");
              const dc = 10 + spell.level;
              let arcana_string = "+0";
              let use_advantage = 0;
              let use_advantages: CharacterAdvantage[] = [];
              let use_gr_adv = false;
        
              if (skill) {
                const { bonuses_string } = char_util.skillRolls(character, skill);
                const { advantage, advantages, greater_advantage } = char_util.getAdvantage(character, "Ability Check", skill);
        
                arcana_string = bonuses_string;
                use_advantage = advantage;
                use_advantages = advantages;
                use_gr_adv = greater_advantage;
              }
        
              const cost2 = new Money();
              switch (cost_unit) {
                case 1: cost2.copper = cost; break;
                case 2: cost2.silver = cost; break;
                case 3: cost2.electrum = cost; break;
                case 4: cost2.gold = cost; break;
                case 5: cost2.platinum = cost; break;
              }
              return (
                <Grid
                  key={key}
                  container
                  spacing={0}
                  direction="row"
                  size={{
                    xs: 12
                  }}>
                  <Grid
                    size={{
                      xs: 6
                    }}>
                    <ButtonBox
                      name={ `Add for ${cost} ${COST_UNIT_MAP[cost_unit]} and Scroll` }
                      disabled={ !enabled || character.money.gold_value() < cost2.gold_value() }
                      onClick={() => {
                        // Removes cost and scroll, and learns spell
                        if (char_item instanceof CharacterBasicItem) {
                          char_item.count--;
                          character.money.subtract(cost2);
                          addSpell(char_class, spell);
                          if (char_item.count === 0) {
                            connect_util.removeItem(character, char_item);
                            // onChange(false, true);
                            onRemove();
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 6
                    }}>
                    <DandDButtonBox
                      name={`Arcana Check: ${arcana_string}, DC ${dc}`}
                      advantage={ use_advantage }
                      advantages={ use_advantages }
                      greater_advantage={ use_gr_adv }
                      onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                        if (char_item.item_type === "Scroll" && char_item.extra_spell) {
                          // const spell = item.extra_spell;
                          // Rolls an Arcana check, and tells you if you succeeded
                          const skill = SKILLS.find(o => o.name === "Arcana");
                          // let dc = 10 + spell.level;
                    
                          if (skill) {
                            const { skill_rolls: rolls } = char_util.skillRolls(character, skill);
                            const { advantage, greater_advantage } = char_util.getAdvantage(character, "Ability Check", skill);
                            const minimum_roll = char_util.getMinimumRoll(character, "Ability Check", skill);
                            // const greater_advantage = character.greater_advantages.filter(o => (o.roll_types.length === 0 || o.roll_types.includes("Ability Check")) && skill.use_ability_score !== "None" && (o.use_ability_scores.length === 0 || o.use_ability_scores.includes(skill.use_ability_score))).length > 0;
                            setRoll(char_util.rollD20({ char_interface: character, rolls, type: "Ability Check", use_ability_score: skill.use_ability_score, advantage, greater_advantage, minimum_roll }));
                          }
                        }
                        setPopoverAnchorEl(event.currentTarget);
                      }}
                    />
                  </Grid>
                  <Grid
                    className="centered"
                    size={{
                      xs: 12
                    }}>
                    <span className='small_label gray'>{ `Takes ${time} ${time_unit_str}` }</span>
                  </Grid>
                </Grid>
              );
            }
            return null;
          })}
          { renderExtras() }
        </Grid>
      );
    }
  }
  return <></>;
}
